/**
 * MaterialRow — one expandable material line in the Materials right pane
 * (`docs/GardEn/handoff-2026-06/garden/screens-materials.jsx` `MaterialRow`).
 *
 * Anatomy: a category Icon, the localized name, a "qty unit · N продавцов"
 * meta line, a `ConfidenceBadge`, and "от {min price}" (or a "no sellers" Pill
 * when the line has no matches). Expanding reveals the seller matches (Avatar +
 * name + price; the cheapest tinted success with a "дешевле всех" tag) and an
 * "Объяснить эту строку" link. A material with a warning shows a tinted warn
 * row under the header.
 *
 * The header is a real `<button>` (focusable, keyboard-activatable,
 * `aria-expanded`) — clicking it toggles expansion and focuses the line in the
 * document pane. Money/qty come from the API as Decimal strings and are parsed
 * once with `parseDecimal`, then formatted with `fmtNum`. Colour is never the
 * sole signal: confidence carries a text label, the cheapest match carries the
 * "дешевле всех" text, and the no-sellers / warn states carry text + icon.
 */
import { useMemo, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Avatar, ConfidenceBadge, Icon, Pill, type IconName } from "@garden/ui";

import { fmtNum, parseDecimal } from "@/lib/format";
import { pickLocalized, type MaterialItem, type SellerMatch } from "@/lib/garden-api";
import { confidenceToLevel } from "@/features/materials/api";
import {
  sellerColor,
  sellerInitials,
} from "@/features/materials/seller-display";

export interface MaterialRowProps {
  material: MaterialItem;
  /** Active UI language (already narrowed to ru/ky/en). */
  lang: string;
  /** Row icon resolved from the material's category bucket. */
  icon: IconName;
  /** Whether this row's seller matches are expanded. */
  expanded: boolean;
  /** Toggle expansion (also focuses this line in the document pane). */
  onToggle: () => void;
}

interface ParsedMatch {
  match: SellerMatch;
  price: number;
}

/**
 * Renders a material row. `data-testid="material-row-{id}"`; the header button
 * is `material-row-header-{id}`; each seller match is `seller-match-{id}-{i}`.
 */
export function MaterialRow({
  material,
  lang,
  icon,
  expanded,
  onToggle,
}: MaterialRowProps): ReactElement {
  const { t } = useTranslation("materials");

  const name = pickLocalized(material.names, lang);
  const unit = pickLocalized(material.unit, lang);
  const qty = parseDecimal(material.qty);
  const sellerCount = material.matches.length;
  const warning = material.warning ? pickLocalized(material.warning, lang) : null;

  // Parse each match price once; sort cheapest-first for the expanded list and
  // derive the "от" minimum.
  const parsedMatches = useMemo<ParsedMatch[]>(
    () =>
      material.matches
        .map((match) => ({ match, price: parseDecimal(match.price_kgs) }))
        .sort((a, b) => a.price - b.price),
    [material.matches],
  );
  const minPrice = parsedMatches.length > 0 ? parsedMatches[0]?.price ?? null : null;

  const confidenceLevel = confidenceToLevel(material.confidence);
  const confidenceLabel = t("confidenceLabel", {
    level: t(`conf.${material.confidence}`),
  });

  return (
    <div
      data-testid={`material-row-${material.id}`}
      className={[
        "overflow-hidden rounded-[var(--g-radius)] border border-border bg-[var(--g-surface)] transition-shadow",
        expanded ? "shadow-[var(--g-shadow-md)]" : "shadow-[var(--g-shadow-sm)]",
      ].join(" ")}
    >
      <button
        type="button"
        data-testid={`material-row-header-${material.id}`}
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={expanded ? t("row.collapse") : t("row.expand")}
        className="flex w-full items-center gap-3 p-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
      >
        <span className="grid size-10 shrink-0 place-items-center rounded-[var(--g-radius)] bg-[var(--g-primary-soft)] text-[var(--g-primary-ink)]">
          <Icon name={icon} size={19} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold leading-tight text-foreground">
            {name}
          </span>
          <span className="mt-1.5 flex flex-wrap items-center gap-2">
            <span className="text-xs tabular-nums text-[var(--g-ink-2)]">
              {fmtNum(qty)} {unit}
            </span>
            <span aria-hidden="true" className="text-xs text-[var(--g-ink-3)]">
              ·
            </span>
            <span className="text-xs text-[var(--g-ink-2)]">
              {t("row.sellers", { count: sellerCount })}
            </span>
            <ConfidenceBadge level={confidenceLevel} label={confidenceLabel} />
          </span>
        </span>
        <span className="shrink-0 text-right">
          {minPrice !== null ? (
            <>
              <span className="block text-[10px] font-semibold text-[var(--g-ink-3)]">
                {t("row.from")}
              </span>
              <span className="block text-[15px] font-bold tabular-nums text-foreground">
                {fmtNum(minPrice)}
              </span>
            </>
          ) : (
            <Pill tone="error" size="sm">
              {t("row.noSellers")}
            </Pill>
          )}
        </span>
      </button>

      {warning !== null && (
        <div
          data-testid={`material-warning-${material.id}`}
          className="mx-3.5 mb-3 flex items-center gap-1.5 rounded-[var(--g-radius)] bg-[color-mix(in_srgb,var(--g-warn)_12%,transparent)] px-2.5 py-2 text-[var(--g-warn)]"
        >
          <Icon name="warn" size={14} className="shrink-0" />
          <span className="text-xs font-medium">{warning}</span>
        </div>
      )}

      {expanded && parsedMatches.length > 0 && (
        <div className="flex flex-col gap-2 px-3.5 pb-3.5">
          {parsedMatches.map(({ match, price }, index) => {
            const cheapest = index === 0;
            return (
              <div
                key={match.seller_id}
                data-testid={`seller-match-${material.id}-${index}`}
                data-cheapest={cheapest ? "true" : "false"}
                className={[
                  "flex items-center gap-2.5 rounded-[var(--g-radius)] px-3 py-2.5",
                  cheapest
                    ? "border border-[color-mix(in_srgb,var(--g-success)_35%,transparent)] bg-[color-mix(in_srgb,var(--g-success)_9%,var(--g-ground))]"
                    : "border border-border bg-[var(--g-ground)]",
                ].join(" ")}
              >
                <Avatar
                  initials={sellerInitials(match.business_name)}
                  color={sellerColor(match.seller_id)}
                  size={32}
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-semibold text-foreground">
                    {match.business_name}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div
                    className={[
                      "text-sm font-bold tabular-nums",
                      cheapest ? "text-[var(--g-success)]" : "text-foreground",
                    ].join(" ")}
                  >
                    {fmtNum(price)}
                  </div>
                  <div className="text-[10px] text-[var(--g-ink-3)]">
                    {t("row.perUnit", { unit })}
                    {cheapest ? ` · ${t("row.cheapest")}` : ""}
                  </div>
                </div>
              </div>
            );
          })}
          <button
            type="button"
            data-testid={`material-explain-${material.id}`}
            className="rounded-[var(--g-radius)] px-0 py-1 text-left text-xs font-bold text-[var(--g-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t("row.explain")} →
          </button>
        </div>
      )}
    </div>
  );
}
